// Storage Service for Google Drive and local export
import { GoogleDriveService } from './googleDriveService';
import GoogleAuthService from './googleAuthService';

interface StoryData {
  topic: string;
  length: string;
  language: string;
  structure: string;
  script: string;
  timestamp: string;
  videoUrl?: string;
}

export class StorageService {

  // Save to Google Drive (Downloads as comprehensive package)
  static async saveToGoogleDrive(data: StoryData): Promise<boolean> {
    try {
      // Try Google Drive API first
      const uploaded = await this.uploadToGoogleDrive(data);
      if (uploaded) {
        return true;
      }
      
      // Fallback to local download
      const htmlContent = this.generateHTMLContent(data);

      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `StoryShot_${data.topic.replace(/\s+/g, '_')}_${Date.now()}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      return true;
    } catch (error) {
      console.error('Error saving to Google Drive:', error);
      return false;
    }
  }

  // Helper function to format script as HTML
  private static formatScriptAsHTML(script: string): string {
    const segments = script.split(/SEGMENT \d+/);
    return segments
      .filter(segment => segment.trim())
      .map((segment, index) => `
        <div class="segment">
          <h3>세그먼트 ${index + 1}</h3>
          <pre>${segment.trim()}</pre>
        </div>
      `)
      .join('');
  }

  // Upload to Google Drive using API
  private static async uploadToGoogleDrive(data: StoryData): Promise<boolean> {
    try {
      // Try new OAuth2 service first
      const authService = GoogleAuthService.getInstance();
      await authService.initialize();
      
      // Generate HTML content
      const htmlContent = this.generateHTMLContent(data);
      const fileName = `StoryShot_${data.topic.replace(/\s+/g, '_')}_${Date.now()}.html`;
      
      // Try to upload with OAuth2
      const uploaded = await authService.uploadToDrive(fileName, htmlContent, 'text/html');
      
      if (uploaded) {
        console.log('Successfully uploaded to Google Drive via OAuth2:', fileName);
        return true;
      }
      
      // Fallback to original Google Drive Service
      const driveService = await GoogleDriveService.initialize();
      
      if (!driveService) {
        console.log('Google Drive Service not available - environment variables missing or incomplete');
        const envCheck = GoogleDriveService.checkEnvironmentVariables();
        if (envCheck.missing.length > 0) {
          console.error('Missing environment variables:', envCheck.missing);
          console.log('Please set these variables in Cloudflare dashboard');
        }
        return false;
      }
      
      // Try to upload to Google Drive with original service
      const uploadedFallback = await driveService.uploadFile(fileName, htmlContent, 'text/html');
      
      if (uploadedFallback) {
        console.log('Successfully uploaded to Google Drive:', fileName);
        return true;
      } else {
        console.log('Google Drive upload failed - OAuth not configured or authentication failed');
        console.log('Falling back to local download');
        return false;
      }
    } catch (error) {
      console.error('Error uploading to Google Drive:', error);
      return false;
    }
  }

  // Generate HTML content as a separate method for reusability
  private static generateHTMLContent(data: StoryData): string {
    return `<!DOCTYPE html>
<html lang="ko">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>StoryShot AI - ${data.topic}</title>
    <style>
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            max-width: 1200px;
            margin: 0 auto;
            padding: 20px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            min-height: 100vh;
        }
        .container {
            background: white;
            border-radius: 12px;
            padding: 40px;
            box-shadow: 0 20px 60px rgba(0,0,0,0.3);
        }
        h1 {
            color: #667eea;
            text-align: center;
            font-size: 2.5em;
            margin-bottom: 30px;
        }
        .metadata {
            background: #f7f9fc;
            padding: 20px;
            border-radius: 8px;
            margin-bottom: 30px;
        }
        .metadata p {
            margin: 10px 0;
            color: #444;
        }
        .script-section {
            margin-top: 30px;
        }
        .script-content {
            background: #f7f9fc;
            padding: 20px;
            border-radius: 8px;
            white-space: pre-wrap;
            line-height: 1.8;
        }
        .segment {
            margin: 20px 0;
            padding: 15px;
            background: #fff;
            border-left: 4px solid #667eea;
        }
        .segment h3 {
            color: #667eea;
            margin-top: 0;
        }
        .segment pre {
            white-space: pre-wrap;
            color: #444;
        }
        .video-section {
            margin-top: 30px;
            padding: 20px;
            background: #f0f4ff;
            border-radius: 8px;
            text-align: center;
        }
        .video-placeholder {
            padding: 40px;
            background: #e0e7ff;
            border-radius: 8px;
            color: #667eea;
        }
        .footer {
            margin-top: 40px;
            text-align: center;
            color: #999;
            font-size: 14px;
        }
    </style>
</head>
<body>
    <div class="container">
        <h1>🎬 StoryShot AI - ${data.topic}</h1>
        
        <div class="metadata">
            <p><strong>📝 주제:</strong> ${data.topic}</p>
            <p><strong>⏱️ 길이:</strong> ${data.length}초</p>
            <p><strong>🌐 언어:</strong> ${data.language}</p>
            <p><strong>📊 구조:</strong> ${data.structure}</p>
            <p><strong>📅 생성일:</strong> ${new Date(data.timestamp).toLocaleString('ko-KR')}</p>
        </div>

        <div class="script-section">
            <h2>📜 스크립트</h2>
            <div class="script-content">
                ${this.formatScriptAsHTML(data.script)}
            </div>
        </div>

        ${data.videoUrl ? `
        <div class="video-section">
            <h2>🎥 생성된 비디오</h2>
            <div class="video-placeholder">
                <p>비디오 URL: <a href="${data.videoUrl}" target="_blank">${data.videoUrl}</a></p>
            </div>
        </div>
        ` : ''}

        <div class="footer">
            <p>Generated by StoryShot AI | ${new Date(data.timestamp).toLocaleString('ko-KR')}</p>
        </div>
    </div>
</body>
</html>`;
  }

  // Export as JSON
  static exportAsJSON(data: StoryData): void {
    const jsonContent = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `StoryShot_${data.topic.replace(/\s+/g, '_')}_${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Export as Markdown
  static exportAsMarkdown(data: StoryData): void {
    const mdContent = `# StoryShot AI - ${data.topic}

## Metadata
- **Length**: ${data.length} seconds
- **Language**: ${data.language}
- **Structure**: ${data.structure}
- **Generated**: ${new Date(data.timestamp).toLocaleString()}

## Script

${data.script}

---
*Generated by StoryShot AI*`;

    const blob = new Blob([mdContent], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `StoryShot_${data.topic.replace(/\s+/g, '_')}_${Date.now()}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}